import type { InteractionCategoryMetadata } from "./queries";

/** Same multiset of topic names; order does not matter. */
export function interactionTopicSelectionMatches(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort((x, y) => x.localeCompare(y));
  const sortedB = [...b].sort((x, y) => x.localeCompare(y));
  return sortedA.every((value, index) => value === sortedB[index]);
}

export function selectionRequiresDefaultPrivateTopics(
  selectedTopicNames: string[],
  categoriesMetadata: InteractionCategoryMetadata[] | undefined | null
): boolean {
  if (!selectedTopicNames.length || !categoriesMetadata?.length) {
    return false;
  }
  const defaultPrivateNames = new Set(categoriesMetadata.filter((c) => c.isDefaultPrivate).map((c) => c.name));
  return selectedTopicNames.some((name) => defaultPrivateNames.has(name));
}

export function shouldSuggestPrivacy(
  selectedTopicNames: string[],
  categoriesMetadata: InteractionCategoryMetadata[] | undefined | null,
  jobPostId: string | null | undefined
): boolean {
  // Job-linked or private-by-default topics suggest privacy
  const jobLinked = Boolean(jobPostId?.length);
  return jobLinked || selectionRequiresDefaultPrivateTopics(selectedTopicNames, categoriesMetadata);
}

/**
 * When topic selection changes, returns how `privacy_restricted` should update.
 * Linking a job is handled in the job picker (`onSelect`); this helper only reacts to topics.
 * - `true`: user added a private-by-default topic.
 * - `null`: keep the user's current privacy toggle (including while a job is linked—topic edits
 *   do not force private on; clearing or changing topics never forces privacy off).
 */
export function shouldUpdatePrivacyForSelection(
  previousCategories: string[],
  newCategories: string[],
  categoriesMetadata: InteractionCategoryMetadata[] | undefined | null
): true | null {
  const defaultPrivateNames = new Set((categoriesMetadata ?? []).filter((c) => c.isDefaultPrivate).map((c) => c.name));

  const added = newCategories.filter((c) => !previousCategories.includes(c));
  if (added.some((name) => defaultPrivateNames.has(name))) {
    return true;
  }

  return null;
}
